import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Trash2, Eye, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PatientFormDialog } from "@/components/PatientFormDialog";

export default function Patients() {
  const { fq, facilities, facilityId, user } = useApp();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = [fq, q ? `q=${encodeURIComponent(q)}` : ""].filter(Boolean).join("&");
    const { data } = await api.get(`/patients${params ? `?${params}` : ""}`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fq, q]);

  const remove = async (id) => {
    try { await api.delete(`/patients/${id}`); toast.success("Moved to recycle bin"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const targetFacility = facilityId === "all" ? facilities[0]?.id : Number(facilityId);

  return (
    <div className="space-y-5" data-testid="patients-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-primary">Master index</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Patients</h1>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input placeholder="Name, UHID or phone" value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-8 w-64" data-testid="patient-search-input" />
          </div>
          <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-patient-button" className="gap-2">
            <Plus className="h-4 w-4" /> Register patient
          </Button>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="patients-table">
            <TableHeader>
              <TableRow>
                <TableHead>UHID</TableHead><TableHead>Name</TableHead><TableHead>Age / Sex</TableHead>
                <TableHead>Phone</TableHead><TableHead>Blood</TableHead><TableHead>BMI</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">
                  No patients yet. Register the first one.
                </TableCell></TableRow>
              )}
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`patient-row-${p.id}`} className="hover:bg-muted/50">
                  <TableCell className="font-mono text-xs">{p.uhid}</TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.age} / {p.gender}</TableCell>
                  <TableCell className="font-mono text-xs">{p.phone || "—"}</TableCell>
                  <TableCell>{p.blood_group ? <Badge variant="secondary">{p.blood_group}</Badge> : "—"}</TableCell>
                  <TableCell className="tabular-nums">{p.bmi || "—"}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="icon" variant="ghost" data-testid={`view-patient-${p.id}`}
                      onClick={() => nav(`/patients/${p.id}`)}><Eye className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`edit-patient-${p.id}`}
                      onClick={() => { setEditing(p); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    {user.role === "super_admin" && (
                      <Button size="icon" variant="ghost" data-testid={`delete-patient-${p.id}`}
                        onClick={() => remove(p.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PatientFormDialog open={open} onOpenChange={setOpen} patient={editing}
        facilityId={targetFacility} onSaved={load} />
    </div>
  );
}
